// functions wrapping google APIs for places autocomplete and place details Services

import React from 'react';
import appConfig from '../../appConfig';

const componentForm = {
	street_number: 'short_name',
	route: 'long_name',
	locality: 'long_name',
	administrative_area_level_1: 'short_name',
	country: 'short_name',
	postal_code: 'short_name',
	subpremise: 'long_name',
};

const mapGoogleToMaster = {
	street_number: 'street_number',
	route: 'street_name',
	locality: 'city',
	administrative_area_level_1: 'state',
	country: 'country',
	postal_code: 'postcode',
	subpremise: 'unit_number',
};

// https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service
export const getPlacePredictions = async (query: string, country: string) => {
	if (google?.maps?.places?.AutocompleteService) {
		const autoCompleteService = new google.maps.places.AutocompleteService();

		try {
			const results = await autoCompleteService.getPlacePredictions({
				input: query,
				componentRestrictions: { country },
				types: ['address'],
			});

			return results.predictions.map((prediction) => ({
				label: prediction.description,
				id: prediction.place_id,
				element: prediction,
			}));
		} catch (e) {
			// muted error, will fall back to empty array
		}
	}
	return [];
};

export const getRegionPredictions = async (query: string, country: string) => {
	if (google?.maps?.places?.AutocompleteService) {
		const autoCompleteService = new google.maps.places.AutocompleteService();
		try {
			const results = await autoCompleteService.getPlacePredictions({
				input: query,
				componentRestrictions: { country },
				types: ['(regions)'],
			});

			return results.predictions.map((prediction) => ({
				label: prediction.description,
				id: prediction.place_id,
				element: prediction,
			}));
		} catch (e) {
			// muted error, will fall back to empty array
		}
	}
	return [];
};

// https://developers.google.com/maps/documentation/javascript/reference/places-service
export const getPlaceDetails = (placeId: google.maps.places.AutocompletePrediction['place_id']) => {
	return new Promise<UserAddress | undefined>((resolve) => {
		if (google?.maps.places.PlacesService) {
			const divTag = document.createElement('div');
			const placesService = new google.maps.places.PlacesService(divTag);

			placesService.getDetails(
				{
					placeId,
					fields: ['address_components', 'formatted_address'],
				},
				(result) => {
					const parsedComponents = parseAddress(result);

					resolve(parsedComponents);
				}
			);
		}
	});
};

export const getRegionDetails = (placeId: google.maps.places.AutocompletePrediction['place_id']) => {
	return new Promise<any>((resolve) => {
		if (google?.maps.places.PlacesService) {
			const divTag = document.createElement('div');
			const placesService = new google.maps.places.PlacesService(divTag);

			placesService.getDetails(
				{
					placeId,
					fields: ['formatted_address,geometry'],
				},
				(res) => {
					resolve({
						displayName: res?.formatted_address,
						latitude: res?.geometry?.location?.lat(),
						longitude: res?.geometry?.location?.lng(),
					});
				}
			);
		}
	});
};

// from google to our form
const parseAddress = (place: google.maps.places.PlaceResult | null) => {
	if (!place || !Array.isArray(place.address_components) || place.address_components.length === 0) {
		return;
	}

	const parsedAddress: UserAddress = {
		unit_number: '',
		street_number: '',
	};

	// Get each component of the address from the place details,
	// and then fill-in the corresponding field on the addressData object.
	place.address_components.forEach((addressComponent) => {
		const componentType = addressComponent.types[0];
		if (componentForm[componentType]) {
			const value = addressComponent[componentForm[componentType]];

			// Hack to split out the street type as API doesn't support it - not 100% accurate
			if (componentType === 'route') {
				const street = value.split(' ');
				parsedAddress.street_type = street.pop();
				parsedAddress[mapGoogleToMaster[componentType]] = street.join(' ');
			} else {
				parsedAddress[mapGoogleToMaster[componentType]] = value;
			}
		}
	});

	return parsedAddress;
};

// Hook to load google maps places lib
// https://developers.google.com/maps/documentation/javascript/places-autocomplete

declare global {
	interface Window {
		geoAddressInit?: () => void;
	}
}

export const useGoogleMapsPlaces = (disabled?: boolean) => {
	const [mapsLibLoaded, setMapsLibLoaded] = React.useState(false);

	React.useEffect(() => {
		if (disabled) return;

		// since this effect doesn't have a clear function to prevent loading the lib multiple times we check if the global variable is available
		if (window.google?.maps?.places) {
			setMapsLibLoaded(true);
			return;
		}

		window.geoAddressInit = () => {
			setMapsLibLoaded(true);
		};

		const script = document.createElement('script');
		script.src = `//maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
			appConfig.googleMapsKey ?? ''
		)}&libraries=places&callback=geoAddressInit`;

		document.body.appendChild(script);
	}, [disabled]);

	return mapsLibLoaded;
};
